/**
 * DreamMee Configuration
 * Character + Car + Scene asset definitions
 * All asset paths are relative to project root (demo-assets/)
 */

// ==================== DEPLOY CONFIG ====================
// To deploy to Shopify, change these 2 lines:
//   1. mode: 'shopify'
//   2. shopifyCdnBase: your store's CDN URL
//
// To deploy with S3-hosted images, set:
//   1. mode: 's3'
//   2. s3CdnBase: your S3 bucket/CloudFront URL (with trailing slash)
//
// All asset paths in this file stay as-is — the resolver
// extracts the filename and prepends the CDN base automatically.
// Shopify Files is a flat namespace, so only filenames matter.
// S3 preserves the directory structure (demo-assets/...).
const DREAMMEE_DEPLOY = {
  mode: 'shopify',   // 'local' | 'shopify' | 's3'
  shopifyCdnBase: 'https://cdn.shopify.com/s/files/1/0935/4844/6011/files/',
  // production: shopifyCdnBase: 'https://cdn.shopify.com/s/files/1/0935/4844/6011/files/'
  s3CdnBase: '',   // e.g. 'https://dreammee-assets.s3.us-west-1.amazonaws.com/'
  maxImageCacheSize: 50, // Centralized LRU cache cap for image assets

  // Event Booth API Settings
  boothApiUrl: 'https://admin.dreammee.dedyn.io/api/orders',
  boothApiKey: 'dm-booth-key-change-me'
};

/**
 * Resolve a local asset path to the correct URL based on deploy mode.
 * - local:   returns the path as-is (e.g. 'demo-assets/character_base_body_standing_1_1.png')
 * - shopify: extracts filename → CDN base + encoded filename
 */
/**
 * Check if we're on the admin page (needs full-resolution assets for print export).
 */
function isAdminPage() {
  return typeof window !== 'undefined' && window.document && window.document.title.includes('Admin');
}

/**
 * Resolve a local asset path to the correct URL based on deploy mode.
 * @param {string} localPath - The local asset path (e.g. 'demo-assets/character_base_body_standing_1_1.png')
 * @param {Object} [options] - Optional settings
 * @param {number} [options.maxWidth] - If provided and on Shopify, append ?width=N to request
 *   a downsized image from the CDN. Ignored on admin pages (full-res needed for print).
 */
function resolveAssetUrl(localPath, options = {}) {
  if (!localPath) return null;
  let path = localPath;

  // On admin page, load the original high-resolution background assets instead of /web/
  if (isAdminPage()) {
    path = path.replace('/web/', '/');
  }

  if (DREAMMEE_DEPLOY.mode === 'shopify') {
    // Build unique filename — for character subdirectories, prefix with folder name
    // e.g. demo-assets/characters/female-gaming-pose/body 1.png -> female-gaming-pose_body_1.webp
    const parts = path.split('/');
    let filename;
    const charIdx = parts.indexOf('characters');
    if (charIdx >= 0 && charIdx < parts.length - 2) {
      // Subdirectory asset: use folder_filename pattern
      const subfolder = parts[charIdx + 1];
      if (subfolder === 'preview') {
        filename = parts[parts.length - 1];
      } else {
        filename = subfolder + '_' + parts[parts.length - 1];
      }
    } else {
      filename = parts[parts.length - 1];
    }
    // Shopify replaces spaces with underscores on upload
    filename = filename.replace(/ /g, '_');
    // Automatically use the webp version on Shopify to save bandwidth
    filename = filename.replace(/\.png$/i, '.webp');
    let url = DREAMMEE_DEPLOY.shopifyCdnBase + encodeURIComponent(filename);
    // Append ?width= for client-side memory savings (skip on admin for print quality)
    if (options.maxWidth && !isAdminPage()) {
      url += '?width=' + Math.round(options.maxWidth);
    }
    return url;
  }

  if (DREAMMEE_DEPLOY.mode === 's3') {
    // S3 preserves directory structure — just prepend the CDN base.
    // Spaces in filenames are URL-encoded for S3 compatibility.
    // The path is used as-is (e.g. 'demo-assets/characters/...')
    const encoded = path.split('/').map(segment => encodeURIComponent(segment)).join('/');
    return DREAMMEE_DEPLOY.s3CdnBase + encoded;
  }

  return path;
}

/**
 * Map a colored asset path to its grayscale equivalent in gray-assets/.
 * Handles three patterns:
 *   1. Tone variants: character_base_body_standing_1_1.png → character_base_body_standing_gray.png
 *   2. Hair tone variants: character_hairstyle_1_1_back.png → character_hairstyle_1_back_gray.png
 *   3. Simple assets: character_nose_1.png → character_nose_1_gray.png
 *   4. Car assets: car-example/car_body.png → car-example/car_body_gray.png
 *   5. Background assets: background-example/design_*.png → background-example/design_*_gray.png
 * Returns null if no gray source exists (e.g. src is null, or 'None' items).
 */
const _grayPathCache = new Map();
function resolveGrayAssetPath(originalSrc) {
  if (!originalSrc) return null;
  if (_grayPathCache.has(originalSrc)) return _grayPathCache.get(originalSrc);

  // Determine the subdirectory and filename
  const parts = originalSrc.split('/');
  const filename = parts.pop();
  const dir = parts.join('/'); // e.g. 'demo-assets' or 'demo-assets/car-example'

  // Build gray filename by inserting _gray before .png
  const grayFilename = filename.replace(/\.png$/, '_gray.png');

  // Build gray path: insert gray-assets/ after demo-assets/
  let grayPath;
  if (dir === 'demo-assets') {
    grayPath = `demo-assets/gray-assets/${grayFilename}`;
  } else if (dir.startsWith('demo-assets/')) {
    const subDir = dir.replace('demo-assets/', '');
    grayPath = `demo-assets/gray-assets/${subDir}/${grayFilename}`;
  } else {
    grayPath = originalSrc; // fallback
  }

  _grayPathCache.set(originalSrc, grayPath);
  return grayPath;
}

/**
 * Maps each gradient-eligible category to its tone group.
 * Each group gets its own independent tone picker in the character creator.
 * Categories not in this map are never gradient-mapped.
 */
const GRADIENT_TONE_GROUPS = {
  // Skin group (body_group accordion)
  'body': 'skin', 'head': 'skin', 'ears': 'skin', 'nose': 'skin',
  // Eyes group (face_group accordion) — independent tone for eye color
  'eyes': 'eyes',
  // Hair group (hair_group accordion)
  'hair_front': 'hair', 'hair_back': 'hair', 'hair_side': 'hair',
  // Clothing group (clothing_group accordion)
  'top_clothing': 'top_clothing', 'bottom_clothing': 'bottom_clothing', 'shoes': 'shoes',
  // New pose components
  'outfit_upper': 'outfit_upper', 'outfit_bottom': 'outfit_bottom', 'outfit_outer': 'outfit_outer', 'socks': 'socks',
  'outfit_shirt': 'outfit_shirt'
};

/** Set of categories eligible for gradient mapping (derived from GRADIENT_TONE_GROUPS). */
const GRADIENT_ELIGIBLE_CATEGORIES = new Set(Object.keys(GRADIENT_TONE_GROUPS));

/** Maps accordion section IDs to their tone group name for UI placement. */
const ACCORDION_TONE_MAP = {
  'body_group': 'skin',
  'hair_group': 'hair',
  'face_group': 'eyes'
};

// ==================== HAIR STYLE SYNC GROUPS ====================
// When hair_front changes, hair_back auto-follows to maintain consistency.
// Color variants removed — gradient mapping handles hair color now.
const HAIR_STYLE_SYNC = [
  { style: 1, front: 'hf1', back: 'hb1' },
  { style: 2, front: 'hf2', back: 'hb2' }
];

// ==================== GRADIENT TONE PRESETS ====================
// 6 global tone presets. Each has 5 stops: shadow / darkMid / mid / lightMid / highlight.
// The selected tone is applied to ALL grayscale assets (character, car, backgrounds).
// 'none' = no gradient mapping, renders original colored assets.
const GRADIENT_PRESETS = [
  {
    id: 'none',
    name: 'Original',
    shadow: null, darkMid: null, mid: null, lightMid: null, highlight: null
  },
  { id: 'tone_black', name: 'Black', highlight: '#707070', lightMid: '#414141', mid: '#111111', darkMid: '#0C0C0C', shadow: '#080808' },
  { id: 'tone_white', name: 'White', highlight: '#F9F9F9', lightMid: '#F7F7F7', mid: '#F5F5F5', darkMid: '#ACACAC', shadow: '#6E6E6E' },
  { id: 'tone_gray', name: 'Gray', highlight: '#B3B3B3', lightMid: '#9A9A9A', mid: '#808080', darkMid: '#5A5A5A', shadow: '#3A3A3A' },
  { id: 'tone_silver', name: 'Silver', highlight: '#D9D9D9', lightMid: '#CDCDCD', mid: '#C0C0C0', darkMid: '#868686', shadow: '#565656' },
  { id: 'tone_brown', name: 'Brown', highlight: '#B99C80', lightMid: '#A27B56', mid: '#8B5A2B', darkMid: '#613F1E', shadow: '#3F2813' },
  { id: 'tone_tan', name: 'Tan', highlight: '#E4D2BA', lightMid: '#DBC3A3', mid: '#D2B48C', darkMid: '#937E62', shadow: '#5E513F' },
  { id: 'tone_beige', name: 'Beige', highlight: '#F0E5D1', lightMid: '#EBDCC2', mid: '#E6D3B3', darkMid: '#A1947D', shadow: '#675F51' },
  { id: 'tone_red', name: 'Red', highlight: '#E67E7E', lightMid: '#DE5353', mid: '#D62828', darkMid: '#961C1C', shadow: '#601212' },
  { id: 'tone_orange', name: 'Orange', highlight: '#FAB266', lightMid: '#F99933', mid: '#F77F00', darkMid: '#AD5900', shadow: '#6F3900' },
  { id: 'tone_yellow', name: 'Yellow', highlight: '#FFE66C', lightMid: '#FFDE3B', mid: '#FFD60A', darkMid: '#B29607', shadow: '#736004' },
  { id: 'tone_green', name: 'Green', highlight: '#81A695', lightMid: '#578872', mid: '#2D6A4F', darkMid: '#1F4A37', shadow: '#143024' },
  { id: 'tone_lime', name: 'Lime', highlight: '#B3D574', lightMid: '#9AC746', mid: '#80B918', darkMid: '#5A8211', shadow: '#3A530B' },
  { id: 'tone_teal', name: 'Teal', highlight: '#66BABA', lightMid: '#33A3A3', mid: '#008C8C', darkMid: '#006262', shadow: '#003F3F' },
  { id: 'tone_cyan', name: 'Cyan', highlight: '#66D2E8', lightMid: '#33C3E0', mid: '#00B4D8', darkMid: '#007E97', shadow: '#005161' },
  { id: 'tone_blue', name: 'Blue', highlight: '#7795E8', lightMid: '#4A72E0', mid: '#1D4ED8', darkMid: '#143797', shadow: '#0D2361' },
  { id: 'tone_navy', name: 'Navy', highlight: '#727A8B', lightMid: '#434E64', mid: '#14213D', darkMid: '#0E172B', shadow: '#090F1B' },
  { id: 'tone_purple', name: 'Purple', highlight: '#B080D9', lightMid: '#9656CC', mid: '#7B2CBF', darkMid: '#561F86', shadow: '#371456' },
  { id: 'tone_pink', name: 'Pink', highlight: '#FF9EC7', lightMid: '#FF7EB5', mid: '#FF5DA2', darkMid: '#B24171', shadow: '#732A49' },
  { id: 'tone_magenta', name: 'Magenta', highlight: '#DF7492', lightMid: '#D4466E', mid: '#C9184A', darkMid: '#8D1134', shadow: '#5A0B21' },
  { id: 'tone_gold', name: 'Gold', highlight: '#E5CF87', lightMid: '#DDBF5F', mid: '#D4AF37', darkMid: '#947A26', shadow: '#5F4F19' }
];

// ==================== SKIN GRADIENT TONE PRESETS ====================
// Separate tone palette for skin.
const SKIN_GRADIENT_PRESETS = [
  {
    id: 'none',
    name: 'Original',
    shadow: null, darkMid: null, mid: null, lightMid: null, highlight: null
  },
  { id: 'skin_ivory', name: 'Ivory', highlight: '#F6E5C6', lightMid: '#EFD7AD', mid: '#E8C994', darkMid: '#CDA46D', shadow: '#A68160' },
  { id: 'skin_fair', name: 'Fair', highlight: '#FFD8AF', lightMid: '#F9C89C', mid: '#F2B889', darkMid: '#D89468', shadow: '#B17659' },
  { id: 'skin_light_beige', name: 'Light Beige', highlight: '#F2D0A3', lightMid: '#E8C08E', mid: '#DDAF78', darkMid: '#C18B56', shadow: '#9B6C48' },
  { id: 'skin_warm_beige', name: 'Warm Beige', highlight: '#F6C28B', lightMid: '#EBB278', mid: '#E0A265', darkMid: '#C17B45', shadow: '#985B39' },
  { id: 'skin_honey', name: 'Honey', highlight: '#EFB86C', lightMid: '#E3A759', mid: '#D69645', darkMid: '#B87335', shadow: '#8B532D' },
  { id: 'skin_caramel', name: 'Caramel', highlight: '#D99652', lightMid: '#CC8745', mid: '#BF7738', darkMid: '#9B572A', shadow: '#73391F' },
  { id: 'skin_brown', name: 'Brown', highlight: '#B7794E', lightMid: '#A86E46', mid: '#99623D', darkMid: '#7A452B', shadow: '#5A2E1E' },
  { id: 'skin_deep_brown', name: 'Deep Brown', highlight: '#8A5A3D', lightMid: '#7C4E36', mid: '#6E422E', darkMid: '#543020', shadow: '#3A1E14' }
];

// ==================== CAR GRADIENT TONE PRESETS ====================
// Separate tone palette for car parts — automotive-inspired 5-stop colors.
// Each car part gets its own independent tone picker.
// Uses blackThreshold to preserve black outlines/details on car assets.
const CAR_GRADIENT_PRESETS = [
  {
    id: 'none',
    name: 'Original',
    shadow: null, darkMid: null, mid: null, lightMid: null, highlight: null
  },
  {
    id: 'car_pearl_white',
    name: 'Pearl White',
    shadow: '#20202A',
    darkMid: '#808090',
    mid: '#C0C0C8',
    lightMid: '#E0E0E8',
    highlight: '#F8F8FF'
  },
  {
    id: 'car_midnight_black',
    name: 'Midnight Black',
    shadow: '#050508',
    darkMid: '#181820',
    mid: '#282830',
    lightMid: '#383845',
    highlight: '#505060'
  },
  {
    id: 'car_racing_red',
    name: 'Racing Red',
    shadow: '#140606',
    darkMid: '#680E18',
    mid: '#BB1122',
    lightMid: '#E03848',
    highlight: '#FF8090'
  },
  {
    id: 'car_british_green',
    name: 'British Green',
    shadow: '#060E06',
    darkMid: '#123820',
    mid: '#1E5E32',
    lightMid: '#388E50',
    highlight: '#70C088'
  },
  {
    id: 'car_champagne_gold',
    name: 'Champagne Gold',
    shadow: '#141008',
    darkMid: '#5C4818',
    mid: '#B08830',
    lightMid: '#D4B060',
    highlight: '#F8E8A8'
  },
  {
    id: 'car_gunmetal',
    name: 'Gunmetal',
    shadow: '#0C0E10',
    darkMid: '#2A3038',
    mid: '#4A5058',
    lightMid: '#687078',
    highlight: '#98A0A8'
  },
  {
    id: 'car_plum_purple',
    name: 'Plum Purple',
    shadow: '#100818',
    darkMid: '#3C1860',
    mid: '#7830A8',
    lightMid: '#A050D0',
    highlight: '#D0A0F0'
  },
  {
    id: 'car_electric_blue',
    name: 'Electric Blue',
    shadow: '#080E1C',
    darkMid: '#143870',
    mid: '#2468D0',
    lightMid: '#4898F0',
    highlight: '#A0D0FF'
  }
];

/** Black luminance threshold for car gradient mapping (pixels ≤ this stay black). */
const CAR_BLACK_THRESHOLD = 12;

/**
 * Maps each gradient-eligible car category to its tone group name.
 * Each category gets its own independent tone picker.
 * Categories not listed here are never gradient-mapped (shadow, tires stay raw).
 */
const CAR_GRADIENT_TONE_GROUPS = {
  'body': 'body',
  'front_bumper': 'front_bumper',
  'front_fender': 'front_fender',
  'rear': 'rear',
  'rear_bumper': 'rear_bumper',
  'rear_fender': 'rear_fender',
  'rear_canard': 'rear_canard',
  'side_skirt': 'side_skirt',
  'extended_front_pieces': 'extended_front_pieces',
  'duckbill': 'duckbill',
  'spoiler': 'spoiler'
};

/** Set of car categories eligible for gradient mapping. */
const CAR_GRADIENT_ELIGIBLE = new Set(Object.keys(CAR_GRADIENT_TONE_GROUPS));


// ==================== CHARACTER CONFIG ====================
const CHARACTER_CONFIG = {
  canvas: {
    width: 460,
    height: 560,
    backgroundColor: 'transparent'
  },

  defaults: {
    pose: 'stand',
    hair_back: 'hb1',
    body: 'b1',
    ears: 'ear1',
    head: 'hd1',
    nose: 'nose1',
    mouth: 'mouth_happy',
    eyes: 'eyes_happy',
    brows: 'brows_happy',
    face_acc: null,
    bottom_clothing: 'pants1',
    top_clothing: 'tshirt1',
    shoes: 'shoes1',
    hair_front: 'hf1',
    extras: null
  },

  // Pose z-index layer order (bottom → top)
  poses: [
    {
      id: 'stand',
      name: 'Standing',
      icon: 'demo-assets/characters/preview/pose_icon_stand.png',
      zIndexConfig: [
        'hair_back', 'body', 'ears', 'head', 'nose',
        'mouth', 'eyes', 'brows', 'face_acc',
        'bottom_clothing', 'top_clothing', 'shoes',
        'hair_front', 'extras'
      ]
    },
    {
      id: 'sit',
      name: 'Sitting',
      icon: 'demo-assets/characters/preview/pose_icon_sit.png',
      zIndexConfig: [
        'hair_back', 'body', 'ears', 'head', 'nose',
        'mouth', 'eyes', 'brows', 'face_acc',
        'bottom_clothing', 'top_clothing', 'shoes',
        'hair_front', 'extras'
      ]
    }
  ],

  // Accordion categories for the character editor UI
  // 'skinSync' means selecting this category triggers skin tone sync
  accordionSections: [
    {
      id: 'pose',
      label: 'Pose',
      icon: '🧍',
      type: 'pose'  // special rendering
    },
    {
      id: 'body_group',
      label: 'Body & Skin',
      icon: '👤',
      subSections: [
        { id: 'body', label: 'Skin Tone' },
        { id: 'ears', label: 'Ears' },
        { id: 'head', label: 'Head Shape' }
      ]
    },
    {
      id: 'face_group',
      label: 'Face',
      icon: '😊',
      subSections: [
        { id: 'nose', label: 'Nose' },
        { id: 'mouth', label: 'Mouth' },
        { id: 'eyes', label: 'Eyes' },
        { id: 'brows', label: 'Eyebrows' },
        { id: 'face_acc', label: 'Blush / Tears' }
      ]
    },
    {
      id: 'clothing_group',
      label: 'Clothing',
      icon: '👕',
      subSections: [
        { id: 'top_clothing', label: 'Top' },
        { id: 'bottom_clothing', label: 'Bottom' },
        { id: 'shoes', label: 'Shoes' }
      ]
    },
    {
      id: 'hair_group',
      label: 'Hair',
      icon: '💇',
      subSections: [
        { id: 'hair_front', label: 'Front' },
        { id: 'hair_back', label: 'Back' }
      ]
    },
    {
      id: 'extras_section',
      label: 'Extras',
      icon: '✨',
      subSections: [
        { id: 'extras', label: 'Accessories' }
      ]
    }
  ],

  // Assets organized by pose → category → items
  // Using local demo-assets paths
  assets: {
    stand: {
      hair_back: [
        { id: 'hb1', name: 'Style 1', src: 'demo-assets/character_hairstyle_1_1_back.png' },
        { id: 'hb2', name: 'Style 2', src: 'demo-assets/character_hairstyle_2_1_back.png' },
        { id: 'hb0', name: 'None', src: null }
      ],
      body: [
        { id: 'b1', name: 'Base', src: 'demo-assets/character_base_body_standing_1_1.png' }
      ],
      ears: [
        { id: 'ear1', name: 'Round', src: 'demo-assets/character_ears_1_1.png' },
        { id: 'ear2', name: 'Pointed', src: 'demo-assets/character_ears_2_1.png' }
      ],
      head: [
        { id: 'hd1', name: 'Shape 1', src: 'demo-assets/character_base_head_1_1.png' },
        { id: 'hd2', name: 'Shape 2', src: 'demo-assets/character_base_head_2_1.png' }
      ],
      nose: [
        { id: 'nose1', name: 'Nose 1', src: 'demo-assets/character_nose_1.png' },
        { id: 'nose2', name: 'Nose 2', src: 'demo-assets/character_nose_2.png' }
      ],
      mouth: [
        { id: 'mouth_happy', name: 'Happy', src: 'demo-assets/character_exp_happy_mouth.png' },
        { id: 'mouth_angry', name: 'Angry', src: 'demo-assets/character_exp_angry_mouth.png' },
        { id: 'mouth_sad', name: 'Sad', src: 'demo-assets/character_exp_sad_mouth.png' }
      ],
      eyes: [
        { id: 'eyes_happy', name: 'Happy', src: 'demo-assets/character_exp_happy_eyes.png' },
        { id: 'eyes_angry', name: 'Angry', src: 'demo-assets/character_exp_angry_eyes.png' },
        { id: 'eyes_sad', name: 'Sad', src: 'demo-assets/character_exp_sad_eyes.png' }
      ],
      brows: [
        { id: 'brows_happy', name: 'Happy', src: 'demo-assets/character_exp_happy_eyebrow.png' },
        { id: 'brows_angry', name: 'Angry', src: 'demo-assets/character_exp_angry_eyebrow.png' },
        { id: 'brows_sad', name: 'Sad', src: 'demo-assets/character_exp_sad_eyebrow.png' }
      ],
      face_acc: [
        { id: 'fa0', name: 'None', src: null },
        { id: 'fa_blush1', name: 'Blush 1', src: 'demo-assets/character_blush_1.png' },
        { id: 'fa_blush2', name: 'Blush 2', src: 'demo-assets/character_blush_2.png' },
        { id: 'fa_sad_misc', name: 'Tears', src: 'demo-assets/character_exp_sad_misc.png' }
      ],
      bottom_clothing: [
        { id: 'pants1', name: 'Pants 1', src: 'demo-assets/character_pants_standing.png' },
        { id: 'pants2', name: 'Pants 2', src: 'demo-assets/character_pants_standing_1.png' },
        { id: 'pants3', name: 'Pants 3', src: 'demo-assets/character_pants_standing_2.png' },
        { id: 'pants4', name: 'Pants 4', src: 'demo-assets/character_pants_standing_3.png' },
        { id: 'bc_aria', name: 'Outfit Aria', src: 'demo-assets/character_outfit_aria_standing_bottom.png' },
        { id: 'bc_miyabi', name: 'Outfit Miyabi', src: 'demo-assets/character_outfit_miyabi_standing_bottom.png' }
      ],
      top_clothing: [
        { id: 'tshirt1', name: 'T-Shirt 1', src: 'demo-assets/character_tshirt_standing.png' },
        { id: 'tshirt2', name: 'T-Shirt 2', src: 'demo-assets/character_tshirt_standing_1.png' },
        { id: 'tshirt3', name: 'T-Shirt 3', src: 'demo-assets/character_tshirt_standing_2.png' },
        { id: 'tshirt4', name: 'T-Shirt 4', src: 'demo-assets/character_tshirt_standing_3.png' },
        { id: 'hoodie1', name: 'Hoodie 1', src: 'demo-assets/character_hoodie_standing.png' },
        { id: 'hoodie2', name: 'Hoodie 2', src: 'demo-assets/character_hoodie_standing_1.png' },
        { id: 'hoodie3', name: 'Hoodie 3', src: 'demo-assets/character_hoodie_standing_2.png' },
        { id: 'hoodie4', name: 'Hoodie 4', src: 'demo-assets/character_hoodie_standing_3.png' },
        { id: 'tc_aria', name: 'Outfit Aria', src: 'demo-assets/character_outfit_aria_standing_top.png' },
        { id: 'tc_miyabi', name: 'Outfit Miyabi', src: 'demo-assets/character_outfit_miyabi_standing_top.png' }
      ],
      shoes: [
        { id: 'shoes1', name: 'Shoes 1', src: 'demo-assets/character_shoes_standing.png' },
        { id: 'shoes2', name: 'Shoes 2', src: 'demo-assets/character_shoes_standing_1.png' },
        { id: 'shoes3', name: 'Shoes 3', src: 'demo-assets/character_shoes_standing_2.png' },
        { id: 'sh_aria', name: 'Outfit Aria', src: 'demo-assets/character_outfit_aria_standing_shoes.png' },
        { id: 'sh_miyabi', name: 'Outfit Miyabi', src: 'demo-assets/character_outfit_miyabi_standing_shoes.png' },
        { id: 'sh0', name: 'None', src: null }
      ],
      hair_front: [
        { id: 'hf1', name: 'Style 1', src: 'demo-assets/character_hairstyle_1_1_front.png' },
        { id: 'hf2', name: 'Style 2', src: 'demo-assets/character_hairstyle_2_1_front.png' },
        { id: 'hf0', name: 'None', src: null }
      ],
      extras: [
        { id: 'ex0', name: 'None', src: null },
        { id: 'ex_angry', name: 'Angry Emote', src: 'demo-assets/character_exp_angry_etc.png' },
        { id: 'ex_watch', name: 'Watch', src: 'demo-assets/character_watch_standing.png' }
      ]
    },

    sit: {
      hair_back: [
        { id: 'hb1', name: 'Style 1', src: 'demo-assets/character_hairstyle_1_1_back.png' },
        { id: 'hb2', name: 'Style 2', src: 'demo-assets/character_hairstyle_2_1_back.png' },
        { id: 'hb0', name: 'None', src: null }
      ],
      body: [
        { id: 'b1', name: 'Base', src: 'demo-assets/character_base_body_sitting_1_1.png' }
      ],
      ears: [
        { id: 'ear1', name: 'Round', src: 'demo-assets/character_ears_1_1.png' },
        { id: 'ear2', name: 'Pointed', src: 'demo-assets/character_ears_2_1.png' }
      ],
      head: [
        { id: 'hd1', name: 'Shape 1', src: 'demo-assets/character_base_head_1_1.png' },
        { id: 'hd2', name: 'Shape 2', src: 'demo-assets/character_base_head_2_1.png' }
      ],
      nose: [
        { id: 'nose1', name: 'Nose 1', src: 'demo-assets/character_nose_1.png' },
        { id: 'nose2', name: 'Nose 2', src: 'demo-assets/character_nose_2.png' }
      ],
      mouth: [
        { id: 'mouth_happy', name: 'Happy', src: 'demo-assets/character_exp_happy_mouth.png' },
        { id: 'mouth_angry', name: 'Angry', src: 'demo-assets/character_exp_angry_mouth.png' },
        { id: 'mouth_sad', name: 'Sad', src: 'demo-assets/character_exp_sad_mouth.png' }
      ],
      eyes: [
        { id: 'eyes_happy', name: 'Happy', src: 'demo-assets/character_exp_happy_eyes.png' },
        { id: 'eyes_angry', name: 'Angry', src: 'demo-assets/character_exp_angry_eyes.png' },
        { id: 'eyes_sad', name: 'Sad', src: 'demo-assets/character_exp_sad_eyes.png' }
      ],
      brows: [
        { id: 'brows_happy', name: 'Happy', src: 'demo-assets/character_exp_happy_eyebrow.png' },
        { id: 'brows_angry', name: 'Angry', src: 'demo-assets/character_exp_angry_eyebrow.png' },
        { id: 'brows_sad', name: 'Sad', src: 'demo-assets/character_exp_sad_eyebrow.png' }
      ],
      face_acc: [
        { id: 'fa0', name: 'None', src: null },
        { id: 'fa_blush1', name: 'Blush 1', src: 'demo-assets/character_blush_1.png' },
        { id: 'fa_blush2', name: 'Blush 2', src: 'demo-assets/character_blush_2.png' },
        { id: 'fa_sad_misc', name: 'Tears', src: 'demo-assets/character_exp_sad_misc.png' }
      ],
      bottom_clothing: [
        { id: 'pants1', name: 'Pants 1', src: 'demo-assets/character_pants_sitting.png' },
        { id: 'pants2', name: 'Pants 2', src: 'demo-assets/character_pants_sitting_1.png' },
        { id: 'pants3', name: 'Pants 3', src: 'demo-assets/character_pants_sitting_2.png' },
        { id: 'pants4', name: 'Pants 4', src: 'demo-assets/character_pants_sitting_3.png' },
        { id: 'bc_aria', name: 'Outfit Aria', src: 'demo-assets/character_outfit_aria_sitting_bottom.png' },
        { id: 'bc_miyabi', name: 'Outfit Miyabi', src: 'demo-assets/character_outfit_miyabi_sitting_bottom.png' }
      ],
      top_clothing: [
        { id: 'tshirt1', name: 'T-Shirt 1', src: 'demo-assets/character_tshirt_sitting.png' },
        { id: 'tshirt2', name: 'T-Shirt 2', src: 'demo-assets/character_tshirt_sitting_1.png' },
        { id: 'tshirt3', name: 'T-Shirt 3', src: 'demo-assets/character_tshirt_sitting_2.png' },
        { id: 'tshirt4', name: 'T-Shirt 4', src: 'demo-assets/character_tshirt_sitting_3.png' },
        { id: 'hoodie1', name: 'Hoodie 1', src: 'demo-assets/character_hoodie_sitting.png' },
        { id: 'hoodie2', name: 'Hoodie 2', src: 'demo-assets/character_hoodie_sitting_1.png' },
        { id: 'hoodie3', name: 'Hoodie 3', src: 'demo-assets/character_hoodie_sitting_2.png' },
        { id: 'hoodie4', name: 'Hoodie 4', src: 'demo-assets/character_hoodie_sitting_3.png' },
        { id: 'tc_aria', name: 'Outfit Aria', src: 'demo-assets/character_outfit_aria_sitting_top.png' },
        { id: 'tc_miyabi', name: 'Outfit Miyabi', src: 'demo-assets/character_outfit_miyabi_sitting_top.png' }
      ],
      shoes: [
        { id: 'shoes1', name: 'Shoes 1', src: 'demo-assets/character_shoes_sitting.png' },
        { id: 'shoes2', name: 'Shoes 2', src: 'demo-assets/character_shoes_sitting_1.png' },
        { id: 'shoes3', name: 'Shoes 3', src: 'demo-assets/character_shoes_sitting_2.png' },
        { id: 'sh_aria', name: 'Outfit Aria', src: 'demo-assets/character_outfit_aria_sitting_shoes.png' },
        { id: 'sh_miyabi', name: 'Outfit Miyabi', src: 'demo-assets/character_outfit_miyabi_sitting_shoes.png' },
        { id: 'sh0', name: 'None', src: null }
      ],
      hair_front: [
        { id: 'hf1', name: 'Style 1', src: 'demo-assets/character_hairstyle_1_1_front.png' },
        { id: 'hf2', name: 'Style 2', src: 'demo-assets/character_hairstyle_2_1_front.png' },
        { id: 'hf0', name: 'None', src: null }
      ],
      extras: [
        { id: 'ex0', name: 'None', src: null },
        { id: 'ex_angry', name: 'Angry Emote', src: 'demo-assets/character_exp_angry_etc.png' },
        { id: 'ex_watch', name: 'Watch', src: 'demo-assets/character_watch_sitting.png' }
      ]
    }
  }
};


// ==================== CAR CONFIG ====================
const CAR_CONFIG = {
  canvas: {
    width: 800,
    height: 657,
    backgroundColor: 'transparent'
  },

  defaults: {
    model: 'gt86',
    shadow: 'shadow_default',
    tire_front: 'tire_front_default',
    body: 'body_default',
    tire_back: 'tire_back_default',
    rear: 'rear_oem',
    rear_fender: null,
    side_skirt: 'side_skirt_oem',
    front_bumper: 'fb_oem',
    front_fender: null,
    duckbill: null,
    spoiler: null,
    extended_front_pieces: null,
    rear_bumper: null,
    tire_rear: 'supra_tire_rear',
    rear_canard: null
  },

  models: [
    {
      id: 'gt86',
      name: 'GT86',
      thumb: 'demo-assets/cars/preview/GT86_1_preview.png',
      zIndexConfig: [
        'shadow', 'body', 'tire_front', 'tire_back', 'rear', 'rear_fender', 'side_skirt', 'front_bumper', 'front_fender', 'duckbill', 'spoiler'
      ]
    },
    {
      id: 'r35',
      name: 'R35',
      thumb: 'demo-assets/cars/preview/GTRR35_1_preview.png',
      zIndexConfig: [
        'shadow', 'body', 'spoiler', 'tire_front', 'tire_back', 'front_bumper', 'extended_front_pieces', 'side_skirt', 'front_fender', 'rear_fender', 'rear_bumper'
      ]
    },
    {
      id: 'supra',
      name: 'Supra MK5',
      thumb: 'demo-assets/cars/preview/Toyota_Supra_MK5_1_preview.png',
      zIndexConfig: [
        'shadow', 'body', 'spoiler', 'tire_front', 'tire_rear', 'front_bumper', 'side_skirt', 'front_fender', 'rear_fender', 'rear_canard'
      ]
    }
  ],

  accordionSections: [
    {
      id: 'model',
      label: 'Car Model',
      icon: '🚗',
      type: 'model'
    },
    {
      id: 'car_body_group',
      label: 'Body & Base',
      icon: '🚗',
      subSections: [
        { id: 'body', label: 'Body' },
        { id: 'shadow', label: 'Shadow' }
      ]
    },
    {
      id: 'car_bumpers',
      label: 'Bumpers & Fenders',
      icon: '🔧',
      subSections: [
        { id: 'front_bumper', label: 'Front Bumper' },
        { id: 'extended_front_pieces', label: 'Extended Front' },
        { id: 'front_fender', label: 'Front Fender' },
        { id: 'rear', label: 'Rear' },
        { id: 'rear_bumper', label: 'Rear Bumper' },
        { id: 'rear_fender', label: 'Rear Fender' },
        { id: 'rear_canard', label: 'Rear Canard' },
        { id: 'side_skirt', label: 'Side Skirt' }
      ]
    },
    {
      id: 'car_extras',
      label: 'Wheels & Spoiler',
      icon: '🏎️',
      subSections: [
        { id: 'tire_front', label: 'Front Wheel' },
        { id: 'tire_back', label: 'Rear Wheel' },
        { id: 'tire_rear', label: 'Rear Wheel (Supra)' },
        { id: 'duckbill', label: 'Duckbill' },
        { id: 'spoiler', label: 'Spoiler' }
      ]
    }
  ],

  assets: {
    gt86: {
      shadow: [
        { id: 'shadow_default', name: 'GT86 Shadow', src: 'demo-assets/car-example/car_shadow.png' },
        { id: 'shadow_none', name: 'None', src: null }
      ],
      body: [
        { id: 'body_default', name: 'GT86 Default', src: 'demo-assets/car-example/car_body.png' }
      ],
      tire_back: [
        { id: 'tire_back_default', name: 'GT86 Default', src: 'demo-assets/car-example/car_tire_back.png' }
      ],
      tire_front: [
        { id: 'tire_front_default', name: 'GT86 Default', src: 'demo-assets/car-example/car_tire_front.png' }
      ],
      front_bumper: [
        { id: 'fb_oem', name: 'GT86 OEM', src: 'demo-assets/car-example/car_front_bumper_oem.png' },
        { id: 'fb_aimgain', name: 'GT86 Aimgain', src: 'demo-assets/car-example/car_front_bumper_aimgain.png' },
        { id: 'fb_pandem', name: 'GT86 Pandem', src: 'demo-assets/car-example/car_front_bumper_pandem.png' }
      ],
      front_fender: [
        { id: 'ff0', name: 'None', src: null },
        { id: 'ff_aimgain', name: 'GT86 Aimgain', src: 'demo-assets/car-example/car_front_fender_aimgain.png' },
        { id: 'ff_pandem', name: 'GT86 Pandem', src: 'demo-assets/car-example/car_front_fender_pandem.png' }
      ],
      rear: [
        { id: 'rear_oem', name: 'OEM', src: null },
        { id: 'rear_aimgain', name: 'Aimgain', src: 'demo-assets/car-example/car_rear_aimgain.png' },
        { id: 'rear_pandem', name: 'Pandem', src: 'demo-assets/car-example/car_rear_pandem.png' }
      ],
      rear_fender: [
        { id: 'rf0', name: 'None', src: null },
        { id: 'rf_aimgain', name: 'GT86 Aimgain', src: 'demo-assets/car-example/car_rear_fender_aimgain.png' },
        { id: 'rf_pandem', name: 'GT86 Pandem', src: 'demo-assets/car-example/car_rear_fender_pandem.png' }
      ],
      side_skirt: [
        { id: 'side_skirt_oem', name: 'GT86 OEM', src: 'demo-assets/car-example/car_side_skirt_oem.png' },
        { id: 'side_skirt_aimgain', name: 'GT86 Aimgain', src: 'demo-assets/car-example/car_side_skirt_aimgain.png' },
        { id: 'side_skirt_pandem', name: 'GT86 Pandem', src: 'demo-assets/car-example/car_side_skirt_pandem.png' }
      ],
      duckbill: [
        { id: 'dk0', name: 'None', src: null },
        { id: 'dk_aimgain', name: 'Ducktail', src: 'demo-assets/car-example/car_spoiler_aimgain_ducktail.png' }
      ],
      spoiler: [
        { id: 'sp0', name: 'None', src: null },
        { id: 'sp_aimgain', name: 'Aimgain', src: 'demo-assets/car-example/car_spoiler_aimgain.png' },
        { id: 'sp_pandem', name: 'Pandem', src: 'demo-assets/car-example/car_spoiler_pandem.png' }
      ]
    },
    r35: {
      shadow: [
        { id: 'r35_shadow', name: 'R35 Shadow', src: 'demo-assets/car-example/car_r35_shadow.png' },
        { id: 'shadow_none', name: 'None', src: null }
      ],
      body: [
        { id: 'body_r35', name: 'R35 Default', src: 'demo-assets/car-example/car_r35_body.png' }
      ],
      spoiler: [
        { id: 'r35_sl_oem', name: 'R35 OEM', src: 'demo-assets/car-example/car_r35_spoiler_oem.png' },
        { id: 'r35_sl_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_spoiler_lbwk.png' },
        { id: 'r35_sl_robot', name: 'R35 Robot', src: 'demo-assets/car-example/car_r35_spoiler_robot.png' },
        { id: 'sp0', name: 'None', src: null }
      ],
      tire_front: [
        { id: 'r35_tire_front', name: 'R35 Default', src: 'demo-assets/car-example/car_r35_tire_front.png' }
      ],
      tire_back: [
        { id: 'r35_tire_back', name: 'R35 Default', src: 'demo-assets/car-example/car_r35_tire_back.png' }
      ],
      front_bumper: [
        { id: 'r35_fb_oem', name: 'R35 OEM', src: 'demo-assets/car-example/car_r35_front_bumper_oem.png' },
        { id: 'r35_fb_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_front_bumper_lbwk.png' },
        { id: 'r35_fb_robot', name: 'R35 Robot', src: 'demo-assets/car-example/car_r35_front_bumper_robot.png' }
      ],
      extended_front_pieces: [
        { id: 'ef0', name: 'None', src: null },
        { id: 'r35_ef_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_extended_front_lbwk.png' }
      ],
      side_skirt: [
        { id: 'r35_ss_oem', name: 'R35 OEM', src: 'demo-assets/car-example/car_r35_sideskirt_oem.png' },
        { id: 'r35_ss_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_sideskirt_lbwk.png' },
        { id: 'r35_ss_robot', name: 'R35 Robot', src: 'demo-assets/car-example/car_r35_sideskirt_robot.png' }
      ],
      front_fender: [
        { id: 'ff0', name: 'None', src: null },
        { id: 'r35_ff_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_front_fender_lbwk.png' },
        { id: 'r35_ff_robot', name: 'R35 Robot', src: 'demo-assets/car-example/car_r35_front_fender_robot.png' }
      ],
      rear_fender: [
        { id: 'rf0', name: 'None', src: null },
        { id: 'r35_rf_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_rear_fender_lbwk.png' },
        { id: 'r35_rf_robot', name: 'R35 Robot', src: 'demo-assets/car-example/car_r35_rear_fender_robot.png' }
      ],
      rear_bumper: [
        { id: 'rb0', name: 'None', src: null },
        { id: 'r35_rb_oem', name: 'R35 OEM', src: 'demo-assets/car-example/car_r35_rear_bumper_oem.png' },
        { id: 'r35_rb_lbwk', name: 'R35 LBWK', src: 'demo-assets/car-example/car_r35_rear_bumper_lbwk.png' }
      ]
    },
    supra: {
      shadow: [
        { id: 'supra_shadow', name: 'Supra Shadow', src: 'demo-assets/car-example/car_supra_shadow.png' },
        { id: 'shadow_none', name: 'None', src: null }
      ],
      body: [
        { id: 'body_supra', name: 'Supra Default', src: 'demo-assets/car-example/car_supra_body.png' }
      ],
      spoiler: [
        { id: 'supra_sl_oem', name: 'Supra OEM', src: 'demo-assets/car-example/car_supra_spoiler_oem.png' },
        { id: 'supra_sl_duck', name: 'Supra Duck Tail', src: 'demo-assets/car-example/car_supra_spoiler_duck_tail.png' },
        { id: 'supra_sl_duck_v', name: 'Supra Duck Veilside', src: 'demo-assets/car-example/car_supra_spoiler_duck_tail_veilside.png' },
        { id: 'sp0', name: 'None', src: null }
      ],
      tire_front: [
        { id: 'supra_tire_front', name: 'Supra Default', src: 'demo-assets/car-example/car_supra_tire_front.png' }
      ],
      tire_rear: [
        { id: 'supra_tire_rear', name: 'Supra Rear', src: 'demo-assets/car-example/car_supra_tire_rear.png' }
      ],
      front_bumper: [
        { id: 'supra_fb_oem', name: 'Supra OEM', src: 'demo-assets/car-example/car_supra_front_bumper_oem.png' },
        { id: 'supra_fb_hks', name: 'Supra HKS', src: 'demo-assets/car-example/car_supra_front_bumper_hks.png' },
        { id: 'supra_fb_veilside', name: 'Supra Veilside', src: 'demo-assets/car-example/car_supra_front_bumper_veilside.png' }
      ],
      side_skirt: [
        { id: 'supra_ss_oem', name: 'Supra OEM', src: 'demo-assets/car-example/car_supra_side_skirt_oem.png' },
        { id: 'supra_ss_hks', name: 'Supra HKS', src: 'demo-assets/car-example/car_supra_side_skirt_hks.png' },
        { id: 'supra_ss_veilside', name: 'Supra Veilside', src: 'demo-assets/car-example/car_supra_side_skirt_veilside.png' }
      ],
      front_fender: [
        { id: 'ff0', name: 'None', src: null },
        { id: 'supra_ff_hks', name: 'Supra HKS', src: 'demo-assets/car-example/car_supra_front_fender_hks.png' },
        { id: 'supra_ff_veilside', name: 'Supra Veilside', src: 'demo-assets/car-example/car_supra_front_fender_veilside.png' }
      ],
      rear_fender: [
        { id: 'rf0', name: 'None', src: null },
        { id: 'supra_rf_hks', name: 'Supra HKS', src: 'demo-assets/car-example/car_supra_rear_fender_hks.png' },
        { id: 'supra_rf_veilside', name: 'Supra Veilside', src: 'demo-assets/car-example/car_supra_rear_fender_veilside.png' }
      ],
      rear_canard: [
        { id: 'rc0', name: 'None', src: null },
        { id: 'supra_rc_hks', name: 'Supra HKS', src: 'demo-assets/car-example/car_supra_rear_canard_hks.png' }
      ]
    }
  }
};


// ==================== DESIGN TEMPLATES ====================
// Each design has a `layerGroups` array defining independently selectable layers.
// Each group has:
//   id:        unique key for this layer slot
//   name:      display name in the UI
//   zPosition: 'below' (behind car/character) or 'above' (in front)
//   order:     render order within its z-position (lower = further back)
//   variants:  array of selectable assets [{ id, name, src }]
//
// This allows users to mix-and-match layers like the character creator.
// Car + Character and Text are injected dynamically by SceneComposer.

const DESIGN_TEMPLATES = [
  // ---- SacAnime Exclusive Design ----
  {
    id: 'sacanime_exclusive',
    name: 'SacAnime Exclusive',
    description: 'SacAnime 2026 Exclusive Edition',
    thumbnail: 'demo-assets/background-example/web/sacanime_exclusive_demo_v3.png',
    canvasSize: { width: 460, height: 602 },  // native 7800×10200 = 13:17
    hasText: false,
    elements: [
      {
        type: 'car',
        slot: 'car_1',
        label: 'Car',
        zOrder: 2,
        defaultPosition: { x: 77, y: 362, scaleX: 0.75, scaleY: 0.75, rotation: 0 }
      }
    ],
    defaultElements: {
      car_1: {
        type: 'premade',
        elementType: 'car',
        premadeId: 'c7_corvette_v3',
        premadeModelId: "c7_corvette",
        premadeSrc: "demo-assets/cars/C7_Corvette_3.png"
      }
    },
    layerGroups: [
      {
        id: 'back',
        name: 'Background',
        zPosition: 'below',
        order: 1,
        variants: [
          { id: 'sace_back', name: 'Default', src: 'demo-assets/background-example/web/sacanime_exclusive_back.png' }
        ]
      },
      {
        id: 'front',
        name: 'Front of Shirt',
        zPosition: 'above',
        order: 99,
        variants: [
          { id: 'sace_front_view', name: 'Default', src: 'demo-assets/background-example/web/sacanime_exclusive_front_view.png' }
        ]
      },
      {
        id: 'overlay',
        name: 'Overlay',
        zPosition: 'above',
        order: 98,
        variants: [
          { id: 'sace_overlay', name: 'Default', src: 'demo-assets/background-example/web/sacanime_exclusive_front.png' }
        ]
      }
    ]
  },

  // ---- SacAnime Custom Design ----
  {
    id: 'sacanime_custom',
    name: 'SacAnime Custom',
    description: 'Custom design with up to 8 characters and 1 car',
    thumbnail: 'demo-assets/background-example/web/sacanime_demo.png',
    textDefaults: {
      text: 'MY SACANIME CUSTOM SHIRT 2026',
      fontFamily: 'Vanilla Caramel',
      fontSize: 20,
      fill: '#FFFFFF',
      x: 98, y: 512, scaleX: 1, scaleY: 1,
      shadow: true
    },
    elements: [
      {
        type: 'character',
        slot: 'character_1',
        label: 'Character 1',
        zOrder: 1,
        defaultPosition: { centerX: 230, centerY: 290, scaleX: 2.01, scaleY: 2.01 }
      },
      {
        type: 'character',
        slot: 'character_2',
        label: 'Character 2',
        zOrder: 2,
        defaultPosition: { centerX: 120, centerY: 300, scaleX: 1.3, scaleY: 1.3 }
      },
      {
        type: 'character',
        slot: 'character_3',
        label: 'Character 3',
        zOrder: 3,
        defaultPosition: { centerX: 340, centerY: 300, scaleX: 1.3, scaleY: 1.3 }
      },
      {
        type: 'character',
        slot: 'character_4',
        label: 'Character 4',
        zOrder: 4,
        defaultPosition: { centerX: 170, centerY: 200, scaleX: 1.1, scaleY: 1.1 }
      },
      {
        type: 'character',
        slot: 'character_5',
        label: 'Character 5',
        zOrder: 5,
        defaultPosition: { centerX: 300, centerY: 200, scaleX: 1.1, scaleY: 1.1 }
      },
      {
        type: 'character',
        slot: 'character_6',
        label: 'Character 6',
        zOrder: 6,
        defaultPosition: { centerX: 100, centerY: 180, scaleX: 1.0, scaleY: 1.0 }
      },
      {
        type: 'character',
        slot: 'character_7',
        label: 'Character 7',
        zOrder: 7,
        defaultPosition: { centerX: 360, centerY: 180, scaleX: 1.0, scaleY: 1.0 }
      },
      {
        type: 'character',
        slot: 'character_8',
        label: 'Character 8',
        zOrder: 8,
        defaultPosition: { centerX: 230, centerY: 140, scaleX: 0.9, scaleY: 0.9 }
      },
      {
        type: 'character',
        slot: 'character_9',
        label: 'Character 9',
        zOrder: 9,
        defaultPosition: { centerX: 100, centerY: 160, scaleX: 0.9, scaleY: 0.9 }
      },
      {
        type: 'car',
        slot: 'car_1',
        label: 'Car',
        zOrder: 10,
        defaultPosition: { x: 30, y: 310, scaleX: 0.8, scaleY: 0.8, rotation: 0 }
      }
    ],
    defaultElements: {
      character_1: {
        type: 'premade',
        elementType: 'character',
        premadeId: 'marin_kitagawa',
        premadeSrc: 'demo-assets/characters/Marin_Kitagawa.png'
      }
    },
    layerGroups: [
      {
        id: 'background',
        name: 'Background',
        zPosition: 'below',
        order: 1,
        variants: [
          { id: 'sac_bg', name: 'Default (Transparent)', src: 'demo-assets/background-example/web/sacanime_bg_transparent.png' }
        ]
      },
      {
        id: 'line',
        name: 'Line',
        zPosition: 'above',
        order: 10,
        draggable: true,
        variants: [
          { id: 'sac_line', name: 'Default', src: 'demo-assets/background-example/web/tdnb_line_cropped.png', defaultPosition: { x: 75, y: 467, width: 309, height: 31 } }
        ]
      }
    ]
  },

  // ---- AX Exclusive Design ----
  // {
  //   id: 'ax_exclusive',
  //   name: 'AX Exclusive',
  //   description: 'Anime Expo 2026 Exclusive Edition',
  //   thumbnail: 'demo-assets/background-example/web/ax_exclusive_demo.png',
  //   canvasSize: { width: 460, height: 602 },  // native 7800×10200 = 13:17
  //   hasText: false,
  //   elements: [
  //     {
  //       type: 'character',
  //       slot: 'character_1',
  //       label: 'Character 1',
  //       zOrder: 2,
  //       defaultPosition: { x: 39, y: 235, scaleX: 1.335, scaleY: 1.335, rotation: 0 }
  //     },
  //     {
  //       type: 'character',
  //       slot: 'character_2',
  //       label: 'Character 2',
  //       zOrder: 5,
  //       defaultPosition: { x: 254, y: 365, scaleX: 1.156, scaleY: 1.156, rotation: 0 }
  //     },
  //     {
  //       type: 'character',
  //       slot: 'character_3',
  //       label: 'Character 3',
  //       zOrder: 4,
  //       defaultPosition: { x: 255, y: 136, scaleX: 1.279, scaleY: 1.279, rotation: 14.2 }
  //     },
  //     {
  //       type: 'car',
  //       slot: 'car_1',
  //       label: 'Car',
  //       zOrder: 3,
  //       defaultPosition: { x: 78, y: 239, scaleX: 0.95, scaleY: 0.95, rotation: 13.9 }
  //     }
  //   ],
  //   defaultElements: {
  //     character_1: {
  //       type: 'premade',
  //       elementType: 'character',
  //       premadeId: 'marin_kitagawa',
  //       premadeSrc: 'demo-assets/characters/Marin_Kitagawa.png'
  //     },
  //     character_2: {
  //       type: 'builder',
  //       elementType: 'character',
  //       pose: 'male_gaming_tilted',
  //       selections: {
  //         pose: 'stand',
  //         hair_back: 'hb1',
  //         body: 'mgp_body',
  //         ears: 'mgp_t_ears',
  //         head: 'mgp_t_head_normal',
  //         nose: 'mgp_t_nose_v1',
  //         mouth: 'mgp_t_mouth_e3',
  //         eyes: 'mgp_t_eyes_e3_v1',
  //         brows: 'mgp_t_brows_e3_v3',
  //         face_acc: 'mgp_t_face_acc_e3_blush',
  //         bottom_clothing: 'pants1',
  //         top_clothing: 'tshirt1',
  //         shoes: 'shoes1',
  //         hair_front: 'mgp_t_hair_front_s3',
  //         extras: null,
  //         hair_side: 'mgp_t_hair_side_s3',
  //         handheld: 'mgp_handheld',
  //         socks: 'mgp_socks_vdefault',
  //         outfit_bottom: 'mgp_s_bottom_set5_bottom_v1',
  //         outfit_acc: 'mgp_s_acc_set1_chain_v1',
  //         outfit_outer: 'mgp_s_outer_set3_sweater_v1',
  //         outfit_upper: 'mgp_s_upper_set1_upper_v1'
  //       },
  //       multiSelections: {
  //         extras: []
  //       },
  //       toneSelections: {
  //         skin: 'skin_warm_beige',
  //         hair: 'none',
  //         eyes: 'none',
  //         top_clothing: 'none',
  //         bottom_clothing: 'none',
  //         shoes: 'none'
  //       }
  //     },
  //     character_3: {
  //       type: 'builder',
  //       elementType: 'character',
  //       pose: 'male_pose_1',
  //       selections: {
  //         pose: 'stand',
  //         hair_back: 'mp1_hair_back_none',
  //         body: 'mp1_body',
  //         ears: 'mp1_ears',
  //         head: 'mp1_head_normal',
  //         nose: 'mp1_nose_v2',
  //         mouth: 'mp1_mouth_e1',
  //         eyes: 'mp1_eyes_e1',
  //         brows: 'mp1_brows_e1',
  //         face_acc: 'mp1_face_acc_cheek',
  //         bottom_clothing: 'pants1',
  //         top_clothing: 'tshirt1',
  //         shoes: 'mp1_a_shoes_set3_vdefault',
  //         hair_front: 'mp1_hair_front_s5',
  //         extras: null,
  //         makeup_lashes: 'misa_makeup_lashes_none',
  //         makeup_lip: 'misa_makeup_lip_none',
  //         expression: 'misa_exp_2',
  //         outfit_bottom: 'mp1_a_bottom_set3_vdefault',
  //         outfit_chain: 'misa_outfit_chain_none',
  //         outfit_outer: 'mgp_s_outer_set1_jacket_v1',
  //         outfit_shirt: 'misa_outfit_shirt_none',
  //         hair_side: 'mp1_hair_side_s5',
  //         handheld: 'mgp_handheld',
  //         socks: 'mgp_socks_vdefault',
  //         outfit_acc: 'mgp_s_acc_set1_chain_v1',
  //         outfit_upper: 'mp1_a_upper_set3_vdefault',
  //         acc_back: 'mp1_acc_back_v1',
  //         acc_front: 'mp1_acc_front_v1'
  //       },
  //       multiSelections: {
  //         extras: []
  //       },
  //       toneSelections: {
  //         skin: 'skin_deep_brown',
  //         hair: 'none',
  //         eyes: 'none',
  //         top_clothing: 'none',
  //         bottom_clothing: 'none',
  //         shoes: 'none'
  //       }
  //     },
  //     car_1: {
  //       type: 'premade',
  //       elementType: 'car',
  //       premadeId: 'c7_corvette_v4',
  //       premadeModelId: 'c7_corvette',
  //       premadeSrc: 'demo-assets/cars/C7_Corvette_4.png'
  //     }
  //   },
  //   layerGroups: [
  //     {
  //       id: 'back',
  //       name: 'Background',
  //       zPosition: 'below',
  //       order: 1,
  //       variants: [
  //         { id: 'axe_back', name: 'Default', src: 'demo-assets/background-example/web/ax_exclusive_back.png' }
  //       ]
  //     },
  //     {
  //       id: 'front',
  //       name: 'Title Overlay',
  //       zPosition: 'above',
  //       order: 1,
  //       variants: [
  //         { id: 'axe_front', name: 'Default', src: 'demo-assets/background-example/web/ax_exclusive_front.png' }
  //       ]
  //     }
  //   ]
  // },
  {
    id: 'valorant',
    name: 'Valorant Theme',
    description: 'Valorant Banner Style',
    thumbnail: 'demo-assets/background-example/web/valorant_demo.png',
    canvasSize: { width: 460, height: 602 },  // native 7800×10200 = 13:17
    textDefaults: {
      text: 'PLAYER NAME',
      fontFamily: 'Valorant',
      fill: '#1A1A2E',
      x: 135, y: 394, scaleX: 0.754, scaleY: 0.754
    },
    elements: [
      {
        type: 'character',
        slot: 'character_1',
        label: 'Character 1',
        zOrder: 2,
        defaultPosition: { centerX: 230, centerY: 296, scaleX: 1.892, scaleY: 1.892 }
      },
      {
        type: 'character',
        slot: 'character_2',
        label: 'Character 2',
        zOrder: 1,
        defaultPosition: { centerX: 350, centerY: 296, scaleX: 1.5, scaleY: 1.5 }
      }
    ],
    defaultElements: {
      character_1: {
        type: 'premade',
        elementType: 'character',
        premadeId: 'valo_killjoy',
        premadeSrc: 'demo-assets/characters/Valo_killjoy.png'
      },
      character_2: {
        type: 'premade',
        elementType: 'character',
        premadeId: 'kdh_derpy',
        premadeSrc: 'demo-assets/characters/KDH_Derpy.png'
      }
    },
    layerGroups: [
      {
        id: 'back',
        name: 'Back',
        zPosition: 'below',
        order: 1,
        variants: [
          { id: 'back_v1', name: 'V1', src: 'demo-assets/background-example/web/valorant_back_v1_2.png' },
          { id: 'back_v2', name: 'V2', src: 'demo-assets/background-example/web/valorant_back_v2_2.png' }
        ]
      },
      {
        id: 'name_plate',
        name: 'Name Plate',
        zPosition: 'above',
        order: 1,
        draggable: true,
        variants: [
          { id: 'np_default', name: 'Default', src: 'demo-assets/background-example/web/valorant_name_plate_cropped.png', defaultPosition: { x: 106, y: 392, width: 248, height: 40 } }
        ]
      }
    ]
  },

  // ---- NorCal FanimeCon Design ----
  // Multi-layer: back + bottom render BELOW car/character,
  // top renders ABOVE car/character.
  // {
  //   id: 'norcal_fanime',
  //   name: 'Fanime 2026 Exclusive Background',
  //   description: 'Exclusive Fanime Design - Yokai Theme',
  //   thumbnail: 'demo-assets/background-example/web/nocmal_fanime_demo.png',
  //   textDefaults: {
  //     fontFamily: 'GT America Compressed Black',
  //     fill: '#FF6B1A'
  //   },
  //   elements: [
  //     {
  //       type: 'car',
  //       slot: 'car_1',
  //       label: 'Car',
  //       defaultPosition: { x: 54, y: 200, scaleX: 0.761, scaleY: 0.761 }
  //     },
  //     {
  //       type: 'character',
  //       slot: 'character_1',
  //       label: 'Character',
  //       defaultPosition: { centerX: 333, centerY: 311 }
  //     }
  //   ],
  //   defaultElements: {
  //     car_1: {
  //       type: 'builder',
  //       elementType: 'car',
  //       model: 'r35',
  //       selections: {
  //         model: 'gt86',
  //         shadow: 'r35_shadow',
  //         tire_front: 'r35_tire_front',
  //         body: 'body_r35',
  //         tire_back: 'r35_tire_back',
  //         rear: 'rear_oem',
  //         rear_fender: 'rf0',
  //         side_skirt: 'r35_ss_oem',
  //         front_bumper: 'r35_fb_oem',
  //         front_fender: 'ff0',
  //         duckbill: null,
  //         spoiler: 'r35_sl_oem',
  //         extended_front_pieces: 'ef0',
  //         rear_bumper: 'rb0',
  //         tire_rear: 'supra_tire_rear',
  //         rear_canard: null
  //       },
  //       toneSelections: {
  //         body: 'none',
  //         front_bumper: 'none',
  //         front_fender: 'none',
  //         rear: 'none',
  //         rear_bumper: 'none',
  //         rear_fender: 'none',
  //         rear_canard: 'none',
  //         side_skirt: 'none',
  //         extended_front_pieces: 'none',
  //         duckbill: 'none',
  //         spoiler: 'none'
  //       }
  //     },
  //     character_1: {
  //       type: 'builder',
  //       elementType: 'character',
  //       pose: 'male_gaming',
  //       selections: {
  //         pose: 'stand',
  //         hair_back: 'hb1',
  //         body: 'mgp_body',
  //         ears: 'mgp_ears',
  //         head: 'mgp_head_fat',
  //         nose: 'mgp_nose_v2',
  //         mouth: 'mgp_mouth_e3',
  //         eyes: 'mgp_eyes_e3_v5',
  //         brows: 'mgp_brows_e1_v1',
  //         face_acc: 'mgp_face_acc_e2_blush',
  //         bottom_clothing: 'pants1',
  //         top_clothing: 'tshirt1',
  //         shoes: 'shoes1',
  //         hair_front: 'mgp_hair_front_s1',
  //         extras: null,
  //         hair_side: 'mgp_hair_side_s1',
  //         handheld: 'mgp_handheld',
  //         socks: 'mgp_socks_vdefault',
  //         outfit_bottom: 'mgp_s_bottom_set5_bottom_v1',
  //         outfit_acc: 'mgp_s_acc_set1_chain_v1',
  //         outfit_outer: 'mgp_s_outer_set3_sweater_v1',
  //         outfit_upper: 'mgp_s_upper_set5_upper_v1'
  //       },
  //       multiSelections: {
  //         extras: []
  //       },
  //       toneSelections: {
  //         skin: 'none',
  //         hair: 'none',
  //         eyes: 'none',
  //         top_clothing: 'none',
  //         bottom_clothing: 'none',
  //         shoes: 'none'
  //       }
  //     }
  //   },
  //   layerGroups: [
  //     {
  //       id: 'back',
  //       name: 'Back',
  //       zPosition: 'below',
  //       order: 1,
  //       variants: [
  //         { id: 'nf_back', name: 'Default', src: 'demo-assets/background-example/web/norcal_fanime_back.png' }
  //       ]
  //     },
  //     {
  //       id: 'bottom',
  //       name: 'Bottom',
  //       zPosition: 'below',
  //       order: 2,
  //       variants: [
  //         { id: 'nf_bottom', name: 'Default', src: 'demo-assets/background-example/web/norcal_fanime_bottom.png' }
  //       ]
  //     },
  //     {
  //       id: 'top',
  //       name: 'Top',
  //       zPosition: 'above',
  //       order: 1,
  //       variants: [
  //         { id: 'nf_top', name: 'Default', src: 'demo-assets/background-example/web/norcal_fanime_top.png' }
  //       ]
  //     }
  //   ],
  //   hasText: false
  // },

  // ---- They Don't Bite Design ----
  {
    id: 'they_dont_bite',
    name: "They Don't Bite",
    description: 'Cute pet-themed design with dogs & cats',
    poses: ['male_tdnb', 'female_tdnb'],
    thumbnail: 'demo-assets/background-example/web/tdbp_demo_v3.png',
    textDefaults: {
      text: "THEY'RE FRIENDLY. JUST AGGRESSIVELY FRIENDLY.",
      fontFamily: 'Vanilla Caramel',
      fontSize: 20,
      fill: '#FFFFFF',
      x: 25, y: 425, scaleX: 1, scaleY: 1,
      shadow: true
    },
    elements: [
      {
        type: 'character',
        slot: 'character_1',
        label: 'Character 1',
        zOrder: 2,
        defaultPosition: { centerX: 230, centerY: 302, scaleX: 2.01, scaleY: 2.01 }
      },
      {
        type: 'character',
        slot: 'character_2',
        label: 'Character 2',
        zOrder: 1,
        defaultPosition: { centerX: 330, centerY: 272, scaleX: 1.0, scaleY: 1.0 }
      }
    ],
    defaultElements: {
      character_1: {
        type: 'builder',
        elementType: 'character',
        pose: 'male_tdnb',
        selections: {
          pose: 'male_tdnb',
          body_shadow: 'mtdnb_body_shadow',
          body: 'mtdnb_body_1',
          ears: 'mtdnb_ear_1',
          head: 'mtdnb_head_1',
          expression: 'mtdnb_exp_1',
          hair_front: 'mtdnb_hair_3',
          socks: 'mtdnb_socks_default',
          outfit_bottom: 'mtdnb_bottom_3_v1',
          outfit_shirt: 'mtdnb_shirt_3_v1',
          outfit_outer: 'mtdnb_outer_3_v1'
        },
        toneSelections: {
          skin: 'none',
          hair: 'none'
        }
      },
      character_2: {
        type: 'premade',
        elementType: 'character',
        premadeId: 'ax_tokai_teio',
        premadeSrc: 'demo-assets/characters/Tokai_Teio.png'
      }
    },
    defaultLayers: {
      dog: 'dog_corgi',
      cat: 'cat_siam'
    },
    layerGroups: [
      {
        id: 'background',
        name: 'Background',
        zPosition: 'below',
        order: 1,
        variants: [
          { id: 'tdnb_bg', name: 'Default (Transparent)', src: 'demo-assets/background-example/web/tdnb_bg_transparent.png' }
        ]
      },
      {
        id: 'cat',
        name: 'Cat',
        zPosition: 'above',
        order: 1,
        draggable: true,
        variants: [
          { id: 'cat_none', name: 'None', src: null },
          { id: 'cat_black', name: 'Black', src: 'demo-assets/background-example/web/tdnb_pets/cat_black_cropped.png', defaultPosition: { x: 32, y: 311, width: 100, height: 80 } },
          { id: 'cat_munchkin', name: 'Munchkin', src: 'demo-assets/background-example/web/tdnb_pets/cat_munchkin_cropped.png', defaultPosition: { x: 32, y: 314, width: 100, height: 77 } },
          { id: 'cat_orange', name: 'Orange', src: 'demo-assets/background-example/web/tdnb_pets/cat_orange_cropped.png', defaultPosition: { x: 32, y: 311, width: 100, height: 80 } },
          { id: 'cat_siam', name: 'Siamese', src: 'demo-assets/background-example/web/tdnb_pets/cat_siam_cropped.png', defaultPosition: { x: 32, y: 311, width: 100, height: 80 } },
          { id: 'cat_tabby', name: 'Tabby', src: 'demo-assets/background-example/web/tdnb_pets/cat_tabby_cropped.png', defaultPosition: { x: 32, y: 311, width: 100, height: 80 } }
        ]
      },
      {
        id: 'dog',
        name: 'Dog',
        zPosition: 'above',
        order: 2,
        draggable: true,
        variants: [
          { id: 'dog_none', name: 'None', src: null },
          { id: 'dog_corgi', name: 'Corgi', src: 'demo-assets/background-example/web/tdnb_pets/dog_corgi_cropped.png', defaultPosition: { x: 316, y: 276, width: 111, height: 104 } },
          { id: 'dog_golden', name: 'Golden Retriever', src: 'demo-assets/background-example/web/tdnb_pets/dog_golden_cropped.png', defaultPosition: { x: 311, y: 225, width: 118, height: 165 } },
          { id: 'dog_husky', name: 'Husky', src: 'demo-assets/background-example/web/tdnb_pets/dog_husky_cropped.png', defaultPosition: { x: 314, y: 236, width: 115, height: 155 } },
          { id: 'dog_pomeranian', name: 'Pomeranian', src: 'demo-assets/background-example/web/tdnb_pets/dog_pomeranian_cropped.png', defaultPosition: { x: 315, y: 298, width: 90, height: 75 } },
          { id: 'dog_shiba', name: 'Shiba Inu', src: 'demo-assets/background-example/web/tdnb_pets/dog_shiba_cropped.png', defaultPosition: { x: 314, y: 280, width: 115, height: 112 } }
        ]
      },
      {
        id: 'line',
        name: 'Line',
        zPosition: 'above',
        order: 10,
        draggable: true,
        variants: [
          { id: 'tdnb_line', name: 'Default', src: 'demo-assets/background-example/web/tdnb_line_cropped.png', defaultPosition: { x: 75, y: 467, width: 309, height: 31 } }
        ]
      }
    ]
  },

  // ---- I Survived AX Design ----
  // {
  //   id: 'i_survived_ax',
  //   name: 'I Survived AX',
  //   description: 'Anime Expo survival-themed design with accessories',
  //   poses: ['female_isa', 'male_isa'],
  //   thumbnail: 'demo-assets/background-example/web/isa_demo_v3.png',
  //   canvasSize: { width: 460, height: 583 },  // native 1200×1520 = 15:19
  //   textDefaults: {
  //     text: 'HOW MY AX 2026 WENT',
  //     fontFamily: 'Vanilla Caramel',
  //     fontSize: 34,
  //     fill: '#FFFFFF',
  //     x: 0, y: 465, scaleX: 1, scaleY: 1,
  //     align: 'center',
  //     width: 460,
  //     shadow: true
  //   },
  //   elements: [
  //     {
  //       type: 'character',
  //       slot: 'character_1',
  //       label: 'Character',
  //       zOrder: 1,
  //       defaultPosition: { centerX: 230, centerY: 286, scaleX: 2.01, scaleY: 2.01 }
  //     }
  //   ],
  //   defaultElements: {
  //     character_1: {
  //       type: 'builder',
  //       elementType: 'character',
  //       pose: 'male_isa',
  //       selections: {
  //         pose: 'male_isa',
  //         body: 'misa_body_1',
  //         ears: 'misa_ear_1',
  //         head: 'misa_head_base_1',
  //         eyes: 'misa_eyes_e2_v1',
  //         brows: 'misa_brows_e2_v1',
  //         mouth: 'misa_mouth_e2',
  //         nose: 'misa_nose_1',
  //         hair_front: 'misa_hair_1',
  //         outfit_bottom: 'misa_bottom_4_v1',
  //         outfit_shirt: 'misa_outfit_shirt_none',
  //         outfit_outer: 'misa_outer_4_hoodie',
  //         outfit_chain: 'misa_outfit_chain_none',
  //         shoes: 'misa_shoes_default',
  //         face_acc: 'misa_face_acc_none',
  //         makeup_lashes: 'misa_makeup_lashes_none',
  //         makeup_lip: 'misa_makeup_lip_none'
  //       },
  //       toneSelections: {
  //         skin: 'none',
  //         hair: 'none'
  //       }
  //     }
  //   },
  //   customLayers: {
  //     enabled: true,
  //     maxCount: 10,
  //     allowedGroups: ['box', 'box_car', 'box_paperbag'],
  //     defaults: [
  //       { group: 'box', variant: 'isa_box_1', defaultPremadeId: 'ax_olruggio' },
  //       { group: 'box_car', variant: 'isa_box_car_1', defaultPremadeId: 'c7_corvette_v1' },
  //       { group: 'box', variant: 'isa_box_2', defaultPremadeId: 'ax_tracer' }
  //     ]
  //   },
  //   layerGroups: [
  //     {
  //       id: 'background',
  //       name: 'Background',
  //       zPosition: 'below',
  //       order: 1,
  //       variants: [
  //         { id: 'isa_bg', name: 'Default', src: 'demo-assets/background-example/web/isa_bg_v2.png' }
  //       ]
  //     },
  //     {
  //       id: 'box',
  //       name: 'Box',
  //       zPosition: 'above',
  //       order: 1,
  //       draggable: true,
  //       isCustomTemplate: true,
  //       premadeOverlay: {
  //         catalog: 'characters',
  //         // Display area ratios within the cropped box where premade is drawn
  //         premadeArea: { x: 0.09, y: 0.10, width: 0.56, height: 0.72 }
  //       },
  //       variants: [
  //         { id: 'isa_box_1', name: 'Box 1', src: 'demo-assets/background-example/web/isa_accessories/box 1_cropped.png', defaultPosition: { x: 102, y: 245, width: 118, height: 114 } },
  //         { id: 'isa_box_2', name: 'Box 2', src: 'demo-assets/background-example/web/isa_accessories/box 2_cropped.png', defaultPosition: { x: 107, y: 81, width: 118, height: 114 } },
  //         { id: 'isa_box_3', name: 'Box 3', src: 'demo-assets/background-example/web/isa_accessories/box 3_cropped.png', defaultPosition: { x: 100, y: 256, width: 118, height: 114 } },
  //         { id: 'isa_box_4', name: 'Box 4', src: 'demo-assets/background-example/web/isa_accessories/box 4_cropped.png', defaultPosition: { x: 100, y: 256, width: 118, height: 114 } },
  //         { id: 'isa_box_5', name: 'Box 5', src: 'demo-assets/background-example/web/isa_accessories/box 5_cropped.png', defaultPosition: { x: 100, y: 256, width: 118, height: 114 } },
  //         { id: 'isa_box_6', name: 'Box 6', src: 'demo-assets/background-example/web/isa_accessories/box 6_cropped.png', defaultPosition: { x: 100, y: 256, width: 118, height: 114 } },
  //         { id: 'isa_box_7', name: 'Box 7', src: 'demo-assets/background-example/web/isa_accessories/box 7_cropped.png', defaultPosition: { x: 100, y: 256, width: 118, height: 114 } },
  //         { id: 'isa_box_8', name: 'Box 8', src: 'demo-assets/background-example/web/isa_accessories/box 8_cropped.png', defaultPosition: { x: 100, y: 256, width: 118, height: 114 } },
  //         { id: 'isa_box_none', name: 'None', src: null },
  //       ]
  //     },
  //     {
  //       id: 'box_car',
  //       name: 'Box Car',
  //       zPosition: 'above',
  //       order: 2,
  //       draggable: true,
  //       isCustomTemplate: true,
  //       premadeOverlay: {
  //         catalog: 'cars',
  //         // Display area ratios within the cropped box car where premade is drawn
  //         premadeArea: { x: 0.05, y: 0.09, width: 0.90, height: 0.85 }
  //       },
  //       variants: [
  //         { id: 'isa_box_car_1', name: 'Car 1', src: 'demo-assets/background-example/web/isa_accessories/box car 1_cropped.png', defaultPosition: { x: 86, y: 175, width: 156, height: 84 } },
  //         { id: 'isa_box_car_2', name: 'Car 2', src: 'demo-assets/background-example/web/isa_accessories/box car 2_cropped.png', defaultPosition: { x: 58, y: 293, width: 156, height: 84 } },
  //         { id: 'isa_box_car_3', name: 'Car 3', src: 'demo-assets/background-example/web/isa_accessories/box car 3_cropped.png', defaultPosition: { x: 58, y: 293, width: 156, height: 84 } },
  //         { id: 'isa_box_car_4', name: 'Car 4', src: 'demo-assets/background-example/web/isa_accessories/box car 4_cropped.png', defaultPosition: { x: 58, y: 293, width: 156, height: 84 } },
  //         { id: 'isa_box_car_5', name: 'Car 5', src: 'demo-assets/background-example/web/isa_accessories/box car 5_cropped.png', defaultPosition: { x: 58, y: 293, width: 156, height: 84 } },
  //         { id: 'isa_box_car_6', name: 'Car 6', src: 'demo-assets/background-example/web/isa_accessories/box car 6_cropped.png', defaultPosition: { x: 58, y: 293, width: 156, height: 84 } },
  //         { id: 'isa_box_car_none', name: 'None', src: null },
  //       ]
  //     },
  //     {
  //       id: 'box_paperbag',
  //       name: 'Paper Bag',
  //       zPosition: 'above',
  //       order: 3,
  //       draggable: true,
  //       isCustomTemplate: true,
  //       variants: [
  //         { id: 'isa_box_paperbag_default', name: 'Default', src: 'demo-assets/background-example/web/isa_accessories/box paperbag_cropped.png', defaultPosition: { x: 189, y: 357, width: 111, height: 147 } },
  //         { id: 'isa_box_paperbag_bangboo', name: 'Bangboo', src: 'demo-assets/background-example/web/isa_accessories/box paperbag bangboo_cropped.png', defaultPosition: { x: 245, y: 350, width: 53, height: 56 } },
  //         { id: 'isa_box_paperbag_black_rabbit', name: 'Black Rabbit', src: 'demo-assets/background-example/web/isa_accessories/box paperbag black rabbit_cropped.png', defaultPosition: { x: 247, y: 347, width: 48, height: 69 } },
  //         { id: 'isa_box_paperbag_frieren', name: 'Frieren', src: 'demo-assets/background-example/web/isa_accessories/box paperbag frieren_cropped.png', defaultPosition: { x: 163, y: 346, width: 89, height: 108 } },
  //         { id: 'isa_box_paperbag_kdh', name: 'KDH', src: 'demo-assets/background-example/web/isa_accessories/box paperbag kdh_cropped.png', defaultPosition: { x: 163, y: 346, width: 89, height: 108 } },
  //         { id: 'isa_box_paperbag_kimetsu', name: 'Kimetsu no Yaiba', src: 'demo-assets/background-example/web/isa_accessories/box paperbag kimetsu no yaiba_cropped.png', defaultPosition: { x: 163, y: 346, width: 89, height: 108 } },
  //         { id: 'isa_box_paperbag_onepiece', name: 'One Piece', src: 'demo-assets/background-example/web/isa_accessories/box paperbag one piece_cropped.png', defaultPosition: { x: 163, y: 346, width: 89, height: 108 } },
  //         { id: 'isa_box_paperbag_pink_rabbit', name: 'Pink Rabbit', src: 'demo-assets/background-example/web/isa_accessories/box paperbag pink rabbit_cropped.png', defaultPosition: { x: 247, y: 347, width: 48, height: 69 } },
  //         { id: 'isa_box_paperbag_umamusume', name: 'Umamusume', src: 'demo-assets/background-example/web/isa_accessories/box paperbag umamusume_cropped.png', defaultPosition: { x: 163, y: 346, width: 89, height: 108 } },
  //         { id: 'isa_box_paperbag_white_rabbit', name: 'White Rabbit', src: 'demo-assets/background-example/web/isa_accessories/box paperbag white rabbit_cropped.png', defaultPosition: { x: 247, y: 347, width: 48, height: 69 } },
  //         { id: 'isa_box_paperbag_none', name: 'None', src: null },
  //       ]
  //     },
  //     {
  //       id: 'line',
  //       name: 'Line',
  //       zPosition: 'above',
  //       order: 10,
  //       draggable: true,
  //       variants: [
  //         { id: 'isa_line', name: 'Default', src: 'demo-assets/background-example/web/isa_bg_line_cropped.png', defaultPosition: { x: 75, y: 525, width: 309, height: 31 } }
  //       ]
  //     }
  //   ]
  // },

  // ---- Gaming Addiction Design ----
  {
    id: 'gaming_addiction',
    name: 'Gaming Addiction',
    description: 'Gaming-themed design with controller & console accessories',
    poses: ['male_gaming', 'male_gaming_tilted', 'female_gaming', 'female_gaming_tilted'],
    thumbnail: 'demo-assets/background-example/web/ga_demo_v3.png',
    textDefaults: {
      text: "I don't have a problem, You Do!",
      fontFamily: 'Vanilla Caramel',
      fontSize: 20,
      fill: '#1890ff',
      x: 68, y: 427, scaleX: 1.322, scaleY: 1.322,
      shadow: true
    },
    elements: [
      {
        type: 'character',
        slot: 'character_1',
        label: 'Character',
        zOrder: 3,
        defaultPosition: { x: 88, y: 64, scaleX: 2.01, scaleY: 2.01, rotation: 0 }
      }
    ],
    defaultElements: {
      character_1: {
        type: 'builder',
        elementType: 'character',
        pose: 'male_gaming',
        selections: {
          pose: 'stand',
          body: 'mgp_body',
          ears: 'mgp_ears',
          head: 'mgp_head_normal',
          nose: 'mgp_nose_v2',
          mouth: 'mgp_mouth_e3',
          eyes: 'mgp_eyes_e3_v5',
          brows: 'mgp_brows_e1_v1',
          face_acc: 'mgp_face_acc_e2_blush',
          hair_front: 'mgp_hair_front_s1',
          hair_side: 'mgp_hair_side_s1',
          handheld: 'mgp_handheld',
          socks: 'mgp_socks_vdefault',
          outfit_bottom: 'mgp_s_bottom_set5_bottom_v1',
          outfit_acc: 'mgp_s_acc_set1_chain_v1',
          outfit_outer: 'mgp_s_outer_set3_sweater_v1',
          outfit_upper: 'mgp_s_upper_set5_upper_v1'
        },
        toneSelections: {
          skin: 'none',
          hair: 'none'
        }
      }
    },
    defaultLayers: {
      item_pos1: 'ga_pos1_v1',
      item_pos2: 'ga_pos2_v1',
      item_pos3: 'ga_pos3_v1',
      item_pos4: 'ga_pos4_v1'
    },
    layerGroups: [
      {
        id: 'background',
        name: 'Background',
        zPosition: 'below',
        order: 1,
        variants: [
          { id: 'ga_bg', name: 'Default (Transparent)', src: 'demo-assets/background-example/web/ga_bg_transparent.png' }
        ]
      },
      {
        id: 'item_pos1',
        name: 'Bottom Right Item',
        zPosition: 'above',
        order: 4,
        draggable: true,
        variants: [
          { id: 'ga_pos1_none', name: 'None', src: null },
          { id: 'ga_pos1_v1', name: 'Boba Tea', src: 'demo-assets/background-example/web/gaming_addiction_items/pos1_v1_cropped.png', defaultPosition: { x: 314, y: 314, scaleX: 1, scaleY: 1, rotation: 0, width: 48, height: 84 } },
          { id: 'ga_pos1_v2', name: 'Tumbler', src: 'demo-assets/background-example/web/gaming_addiction_items/pos1_v2_cropped.png', defaultPosition: { x: 366, y: 430, width: 52, height: 95 } },
          { id: 'ga_pos1_v3', name: 'Soda Can', src: 'demo-assets/background-example/web/gaming_addiction_items/pos1_v3_cropped.png', defaultPosition: { x: 382, y: 465, width: 33, height: 61 } },
          { id: 'ga_pos1_v4', name: 'Xbox Controller', src: 'demo-assets/background-example/web/gaming_addiction_items/pos1_v4_cropped.png', defaultPosition: { x: 362, y: 489, width: 83, height: 64 } },
          { id: 'ga_pos1_v5', name: 'PS5 Controller', src: 'demo-assets/background-example/web/gaming_addiction_items/pos1_v5_cropped.png', defaultPosition: { x: 358, y: 492, width: 91, height: 45 } },
          { id: 'ga_pos1_v6', name: 'Switch Joy-Cons', src: 'demo-assets/background-example/web/gaming_addiction_items/pos1_v6_cropped.png', defaultPosition: { x: 364, y: 470, width: 62, height: 60 } }
        ]
      },
      {
        id: 'item_pos2',
        name: 'Left Item',
        zPosition: 'above',
        order: 2,
        draggable: true,
        variants: [
          { id: 'ga_pos2_none', name: 'None', src: null },
          { id: 'ga_pos2_v1', name: 'PS5 Console', src: 'demo-assets/background-example/web/gaming_addiction_items/pos2_v1_cropped.png', defaultPosition: { x: 53, y: 229, scaleX: 1, scaleY: 1, rotation: 0, width: 97, height: 152 } },
          { id: 'ga_pos2_v2', name: 'Game Discs', src: 'demo-assets/background-example/web/gaming_addiction_items/pos2_v2_cropped.png', defaultPosition: { x: 17, y: 411, width: 82, height: 70 } },
          { id: 'ga_pos2_v3', name: 'PC Tower', src: 'demo-assets/background-example/web/gaming_addiction_items/pos2_v3_cropped.png', defaultPosition: { x: 4, y: 320, width: 115, height: 154 } },
          { id: 'ga_pos2_v4', name: 'Gaming PC', src: 'demo-assets/background-example/web/gaming_addiction_items/pos2_v4_cropped.png', defaultPosition: { x: 24, y: 298, width: 114, height: 177 } }
        ]
      },
      {
        id: 'item_pos3',
        name: 'Right Item',
        zPosition: 'above',
        order: 5,
        draggable: true,
        variants: [
          { id: 'ga_pos3_none', name: 'None', src: null },
          { id: 'ga_pos3_v1', name: 'Headset', src: 'demo-assets/background-example/web/gaming_addiction_items/pos3_v1_cropped.png', defaultPosition: { x: 99, y: 338, scaleX: 0.629, scaleY: 0.629, rotation: 0, width: 155, height: 132 } },
          { id: 'ga_pos3_v2', name: 'Manga Stack', src: 'demo-assets/background-example/web/gaming_addiction_items/pos3_v2_cropped.png', defaultPosition: { x: 6, y: 482, width: 190, height: 60 } },
          { id: 'ga_pos3_v3', name: 'Chip Bag', src: 'demo-assets/background-example/web/gaming_addiction_items/pos3_v3_cropped.png', defaultPosition: { x: 29, y: 475, width: 115, height: 74 } },
          { id: 'ga_pos3_v4', name: 'SNES Console', src: 'demo-assets/background-example/web/gaming_addiction_items/pos3_v4_cropped.png', defaultPosition: { x: 18, y: 476, width: 147, height: 77 } }
        ]
      },
      {
        id: 'item_pos4',
        name: 'Floor Item',
        zPosition: 'above',
        order: 1,
        draggable: true,
        variants: [
          { id: 'ga_pos4_none', name: 'None', src: null },
          { id: 'ga_pos4_v1', name: 'Delivery Boxes', src: 'demo-assets/background-example/web/gaming_addiction_items/pos4_v1_cropped.png', defaultPosition: { x: 278, y: 247, scaleX: 1, scaleY: 1, rotation: 0, width: 171, height: 133 } },
          { id: 'ga_pos4_v2', name: 'Game Stack', src: 'demo-assets/background-example/web/gaming_addiction_items/pos4_v2_cropped.png', defaultPosition: { x: 322, y: 299, width: 131, height: 190 } },
          { id: 'ga_pos4_v3', name: 'Laundry Basket', src: 'demo-assets/background-example/web/gaming_addiction_items/pos4_v3_cropped.png', defaultPosition: { x: 267, y: 320, width: 183, height: 161 } }
        ]
      },
      {
        id: 'line',
        name: 'Line',
        zPosition: 'above',
        order: 10,
        draggable: true,
        variants: [
          { id: 'ga_line', name: 'Default', src: 'demo-assets/background-example/web/isa_bg_line_cropped.png', defaultPosition: { x: 75, y: 478, scaleX: 1, scaleY: 1, rotation: 0, width: 309, height: 31 } }
        ]
      }
    ]
  }
];

/**
 * Resolve active layers from a design + layerSelections map.
 * Returns { below: [...], above: [...] } sorted by order,
 * compatible with the rendering pipeline.
 *
 * @param {string} designId
 * @param {Object} layerSelections  e.g. { back: 'back_v1', front: 'front_v1', name_plate: 'np_default' }
 * @param {string} [activePose]     Optional current character pose to resolve dynamic pose-specific sources
 */
function resolveDesignLayers(designId, layerSelections, activePose = null) {
  if (typeof DESIGN_TEMPLATES === 'undefined') return { below: [], above: [] };
  const design = DESIGN_TEMPLATES.find(d => d.id === designId);
  if (!design || !design.layerGroups) return { below: [], above: [] };

  const below = [];
  const above = [];

  // Helper to resolve pose-specific src
  const resolveSrc = (src) => {
    if (src && typeof src === 'object') {
      const poseKey = activePose || 'male_tdnb';
      return src[poseKey] || Object.values(src)[0];
    }
    return src;
  };

  for (const group of design.layerGroups) {
    // Skip custom template groups — they are rendered via instance keys
    if (group.isCustomTemplate) continue;

    const selectedId = layerSelections && layerSelections[group.id];
    // Find selected variant, or fall back to defaultLayers if defined, or first variant
    const variant = selectedId
      ? group.variants.find(v => v.id === selectedId)
      : (design.defaultLayers && design.defaultLayers[group.id]
        ? group.variants.find(v => v.id === design.defaultLayers[group.id])
        : group.variants[0]);
    if (!variant) continue;

    const src = resolveSrc(variant.src);
    if (!src) continue;

    const entry = { src, order: group.order, groupId: group.id };
    if (group.zPosition === 'above') {
      above.push(entry);
    } else {
      below.push(entry);
    }
  }

  // Resolve instance keys (e.g. 'box:0', 'box_car:2') from layerSelections
  if (layerSelections) {
    for (const key of Object.keys(layerSelections)) {
      const colonIdx = key.indexOf(':');
      if (colonIdx <= 0) continue;
      const baseId = key.substring(0, colonIdx);
      const group = design.layerGroups.find(g => g.id === baseId && g.isCustomTemplate);
      if (!group) continue;

      const variant = group.variants.find(v => v.id === layerSelections[key]);
      if (!variant) continue;
      const src = resolveSrc(variant.src);
      if (!src) continue;

      const entry = { src, order: group.order, groupId: key };
      if (group.zPosition === 'above') {
        above.push(entry);
      } else {
        below.push(entry);
      }
    }
  }

  below.sort((a, b) => a.order - b.order);
  above.sort((a, b) => a.order - b.order);

  return { below, above };
}

/**
 * Get the elements array from a design configuration.
 * Each element: { type: 'character'|'car', slot: string, label: string, defaultPosition: {...}, zOrder?: number }
 * 
 * zOrder controls rendering order in the scene (higher = on top). If omitted, array index is used.
 * This allows character_1 to be first in the array (built first) but still render on top.
 * 
 * Designs may also have a `defaultElements` object mapping slot names to default configs:
 *   - Premade: { type: 'premade', elementType, premadeId, premadeSrc, premadeModelId? }
 *   - Builder: { type: 'builder', elementType, pose?, model?, selections, toneSelections?, multiSelections? }
 *
 * @param {Object} design  A DESIGN_TEMPLATES entry
 * @returns {Array}  Elements array (may be empty)
 */
function getDesignElements(design) {
  if (!design) return [];
  return design.elements || [];
}

// ==================== SCENE CONFIG ====================
const SCENE_CONFIG = {
  canvas: {
    width: 460,
    height: 560
  },

  textDefaults: {
    text: 'YOUR TEXT',
    fontFamily: 'Outfit',
    fontSize: 32,
    fill: '#FFFFFF',
    x: 130,
    y: 120,
    shadow: false
  }
};

// ==================== SHARED SCENE SIZING ====================
// Premade assets are 3500×3000. These constants define the target bounding
// box each asset type is scaled into on the scene canvas.
// Both frontend (step-environment.js) and admin (admin.js) use these so
// sizing logic is maintained in one place.

const SCENE_CHARACTER_FIT_W = 200;
const SCENE_CHARACTER_PREMADE_H = 3000 * (SCENE_CHARACTER_FIT_W / 3500); // ≈171
const SCENE_CAR_FIT_W = 380;

/**
 * Compute { width, height } for a character image on the scene canvas.
 * Works for both premade PNGs and custom builder snapshots.
 * Premade (3500×3000) → 200×171.  Custom (460×560) → ~140×171.
 */
function sceneCharacterSize(imgWidth, imgHeight) {
  const scale = Math.min(SCENE_CHARACTER_FIT_W / imgWidth, SCENE_CHARACTER_PREMADE_H / imgHeight);
  return { width: imgWidth * scale, height: imgHeight * scale };
}

/**
 * Compute { width, height } for a car image on the scene canvas.
 * Scales to fit SCENE_CAR_FIT_W wide, preserving aspect ratio.
 * Premade (3500×3000) → 380×326.  Custom (800×657) → 380×312.
 */
function sceneCarSize(imgWidth, imgHeight) {
  const scale = SCENE_CAR_FIT_W / imgWidth;
  return { width: imgWidth * scale, height: imgHeight * scale };
}
